import { AppEmbed, EmbedEvent, HomePageSearchBarMode } from "@thoughtspot/visual-embed-sdk";
import { Action } from "@thoughtspot/visual-embed-sdk";
import { IThoughtspotSdkAppEmbedProps } from "./component";
import { pageURIs, bestPracticesDocLinks } from "./constants";
import React, { lazy, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMemo } from "react";
import { isEqual } from "lodash";
import "./index.scss";
import ViewCARCMappingsPanel, {
  toggleViewCarcMappingsPanelOpen,
} from "./view-carc-mappings";
import { TOGGLE_VIEW_CARAC_MAPPINGS_PANEL } from "./reducers/view-carc-mappings-reducer";
import { SET_MDAE_NAVIGATION_BAR_CONFIG } from "../common_widgets/navigation_bar/reducers/mdae-navigation-bar-reducer";
import {
  CURRENT_LIVEBOARD,
  SET_AI_SUMMARY,
} from "./reducers/ai-summary-reducer";
import { GET_AI_SUMMARY } from "./services/get-ai-summary";
import {  callOperation } from "async-ops";
import { createAuditFromRevenueRiskLiveboard } from "./common-functions";
import { SHOW_LOADER } from "../mdae-common-reducer";
import { getUserPermissionObject } from "../user-permissions-helper";

const TargetedDenialAnalysisReports = lazy(() =>
  import("../targeted-denial-analysis")
);

type TFrameParams = {
  height: any;
  width: any;
};

const isTargetedDenialAnalysisLiveboard: (liveboardName: string) => boolean = (
  liveboardName: string
) => {
  if (liveboardName === undefined) {
    return false;
  }
  if (
    liveboardName === "TargetedDenialAnalysis-Professional" ||
    liveboardName === "TargetedDenialAnalysis-Hospital"
  ) {
    return true;
  }
  return false;
};

export const getLiveboardGuid: (
  liveboardName: string,
  objectList: any
) => string = (liveboardName: string, objectList: any) => {
  let guid: string = "";

  if (liveboardName) {
    guid = objectList?.find((x) => x.key === liveboardName).value;
  }
  return guid;
};

const getTSEmbedFrameParams: (liveboardName: string) => TFrameParams = (
  liveboardName: string
) => {
  // get screen height from window object

  let height = window.innerHeight - 40 - 56 - 61;

  if (isTargetedDenialAnalysisLiveboard(liveboardName)) {
    height = height - 37;
  }
  return {
    height: height,
    width: "100%",
  };
};

const getPageUriGivenName: (
  pageName: string,
  objectList: any,
  liveboardName?: string
) => string = (pageName: string, objectList: any, liveboardName?: string) => {
  let guid: string = getLiveboardGuid(liveboardName, objectList);

  if (pageName === "liveboard" && guid !== "") {
    return `${pageURIs[pageName]}/${guid}`;
  }
  return pageURIs[pageName];
};



const getEmbedProperties = (
  props: IThoughtspotSdkAppEmbedProps,
  objectList: any,
  auditPermission: any
) => {
  const hiddenActions = [
    Action.EditTML,
    Action.ImportTML,
    Action.ExportTML,
    Action.UpdateTML,
    Action.CopyLink,
    Action.Schedule,
    Action.RequestVerification,
    Action.SyncToOtherApps,
    Action.SyncToSheets,
    Action.ManagePipelines,
    Action.CreateMonitor,
    Action.AddToWatchlist,
    Action.RemoveFromWatchlist
  ];
  if (
    props?.liveboardName?.toLowerCase() === "auditroi" ||
    props?.liveboardName?.toLowerCase() === "payerauditroi"
  ) {
    hiddenActions.push(Action.DownloadAsPdf);
    hiddenActions.push(Action.DownloadAsCsv);
    hiddenActions.push(Action.DownloadAsPng);
    hiddenActions.push(Action.MakeACopy);
    hiddenActions.push(Action.Share);
    hiddenActions.push(Action.Present);
    hiddenActions.push(Action.Edit);
    hiddenActions.push(Action.AxisMenuRename);
    hiddenActions.push(Action.AnswerDelete);
    hiddenActions.push(Action.RenameModalTitleDescription);
    hiddenActions.push(Action.DrillExclude);
    hiddenActions.push(Action.CustomizeHeadlines);
    hiddenActions.push(Action.Remove);
    hiddenActions.push(Action.ShowUnderlyingData);
    hiddenActions.push(Action.Download);
    hiddenActions.push(Action.Explore);
    hiddenActions.push(Action.Pin);
    hiddenActions.push(Action.SyncToOtherApps);
    hiddenActions.push(Action.SyncToSheets);
    hiddenActions.push(Action.SpotIQAnalyze); 
    hiddenActions.push(Action.Share);  
    hiddenActions.push(Action.DrillDown);
    hiddenActions.push(Action.CrossFilter); 
    hiddenActions.push(Action.AIHighlights); 
    hiddenActions.push(Action.RequestVerification);
    hiddenActions.push(Action.AnswerDelete);
    hiddenActions.push(Action.AskAi);
    hiddenActions.push(Action.AxisMenuRemove);
  }  
  if (!auditPermission.hasCreateAccess) {
    hiddenActions.push("create-audit" as any);
  }
  return {
    frameParams: getTSEmbedFrameParams(props.liveboardName),
    path: getPageUriGivenName(
      props.landingPage,
      objectList,
      props.liveboardName
    ),
    hiddenActions: hiddenActions,
    isUnifiedSearchExperienceEnabled: false,
    homePageSearchBarMode: HomePageSearchBarMode.AI_ANSWER
  };
};

const getDocLinkFromModule = (module) => {
  return bestPracticesDocLinks[module];
};

const TsFullAppEmbed: React.FC<IThoughtspotSdkAppEmbedProps> = (props) => {
  const dispatch = useDispatch();
  const thoughtSpotSettings = useSelector(
    (state) => state["mdaeNavigationBarReducer"].thoughtSpotSettings,
    isEqual
  );
  const currentUser = useSelector(
    (state) => state["mdaeNavigationBarReducer"].currentUser,
    isEqual
  );

  const auditPermission = getUserPermissionObject("AUDIT", currentUser);     

  const embedRef = useRef<AppEmbed | null>(null);

  const bestPracticesDocLink = useMemo(() => {
    if (props.landingPage !== "home") {
      return null;
    }

    return getDocLinkFromModule(window.location.search);
  }, [window.location.search, props.landingPage]);

    useEffect(() => {
        if (
            thoughtSpotSettings !== undefined &&
            thoughtSpotSettings.objectList !== undefined
        ) {

            const embed = new AppEmbed(
                "#ts-full-embed",
                getEmbedProperties(
                    props,
                    thoughtSpotSettings.objectList,
                    auditPermission
                )
            );
            // attach custom actions here
            embedRef.current = embed; // <== save reference

      embed.on(EmbedEvent.CustomAction, async (e: any) => {
        dispatch({ type: SHOW_LOADER });
        if (e.data.id === "create-audit") {
          const underlying = await e.answerService.getUnderlyingDataForPoint([
            "Account Number",
          ]);
          var fetchedData = await underlying.fetchData(0, 1000);
          const accountNumberIndex = fetchedData?.columns?.findIndex(
            (col) => col.column?.name === "Account Number"
          );
          localStorage.removeItem("Denial_Risks_Account_Cookie");
          createAuditFromRevenueRiskLiveboard(
            dispatch,
            fetchedData?.data?.columnDataLite[accountNumberIndex]?.dataValue,
            currentUser
          );
        }
      });
      embed.render();
    }
  }, [thoughtSpotSettings, props, auditPermission]);

  useEffect(() => {
    if (isViewCARCMappingPanelOpen) {
      dispatch({
        type: TOGGLE_VIEW_CARAC_MAPPINGS_PANEL,
        data: {
          isViewCARCMappingPanelOpen: false,
        },
      });
    }
  }, [window.location.href]);

  const isViewCARCMappingPanelOpen = useSelector(
    (state) => state["viewCARCMappingsReducer"].isViewCARCMappingPanelOpen
  );
  const mdaePageTitle = useSelector(
    (state: any) => state.mdaeNavigationBarReducer.mdaePageTitle
  );
  const isMdaeNavigationBarExpanded = useSelector(
    (state) => state["mdaeNavigationBarReducer"].isMdaeNavigationBarExpanded,
    isEqual
  );

  const isFromROISummary =
    props?.liveboardName?.toLowerCase() === "auditroi" ||
    props?.liveboardName?.toLowerCase() === "payerauditroi";

  const buttonRight = isViewCARCMappingPanelOpen
    ? isFromROISummary
      ? "420px"
      : "630px"
    : "5px";

  const onDrawerClick = (isViewCARCMappingPanelOpen, dispatch) => {
    toggleViewCarcMappingsPanelOpen(isViewCARCMappingPanelOpen, dispatch);    
  };
  const loadAISummary = useCallback(async () => {
    let liveBoardId = "";
    dispatch({
      type: SET_AI_SUMMARY,
      data: {
        summaryText: "",
        createdDate: "",
      },
      liveBoardId: liveBoardId,
    });
    if (
      props?.liveboardName != undefined &&
      (props?.liveboardName?.toLowerCase() === "auditroi" ||
        props?.liveboardName?.toLowerCase() === "payerauditroi")
    ) {
      liveBoardId = getLiveboardGuid(
        props?.liveboardName?.toLowerCase(),
        thoughtSpotSettings?.objectList
      );
    }
    if (liveBoardId) {
      const response = await callOperation(GET_AI_SUMMARY, { liveBoardId });
      if (
        response &&
        response?.aiSummary &&
        response?.aiSummary?.liveBoardId === liveBoardId
      ) {
        dispatch({
          type: SET_AI_SUMMARY,
          data: {
            summaryText: response.aiSummary.summaryText,
            createdDate: response.aiSummary.createdDate,
          },
        });
        dispatch({
          type: CURRENT_LIVEBOARD,
          data: {
            liveBoardId: liveBoardId,
            liveBoardName: props?.liveboardName,
          },
        });
        const createdDate = response.aiSummary.createdDate.split("T")[0];
        const todayDate = new Date().toISOString().split("T")[0];
        if (createdDate == todayDate) {
          onDrawerClick(false, dispatch);
        }
      }
    }
  },[
    dispatch,
    props?.liveboardName,
    thoughtSpotSettings?.objectList
  ]);
  useEffect(() => {
    if (
      isFromROISummary &&
      thoughtSpotSettings &&
      thoughtSpotSettings?.objectList?.length
    ) {
      loadAISummary();
    }
  }, [isFromROISummary, thoughtSpotSettings, props?.liveboardName]);

  useEffect(() => {
    if (isFromROISummary) {
      dispatch({
        type: SET_MDAE_NAVIGATION_BAR_CONFIG,
        data: {
          isMdaeNavigationBarExpanded: !isViewCARCMappingPanelOpen,
        },
      });
    }
  }, [
    isFromROISummary,
    isViewCARCMappingPanelOpen,
    isMdaeNavigationBarExpanded,
  ]);

  return (    
    <ViewCARCMappingsPanel isFromROISummary={isFromROISummary}>
      <div
        id="ApiIntegrationBasic"
        className="row remove-margin maudit-widget-background"
      >
        <div className="col-xs-12 remove-padding">
          <div className="row remove-margin">
            {bestPracticesDocLink && (
              <div
                id="best-practices-link"
                style={{ height: "30px", backgroundColor: "#E5E5E5" }}
              >
                <a
                  style={{
                    color: "#2770ef",
                    position: "absolute",
                    paddingTop: "11px",
                    paddingLeft: "40px",
                    fontWeight: "bold",
                    textDecoration: "underline",
                    letterSpacing: ".5px",
                  }}
                  href={bestPracticesDocLink}
                  target="_blank"
                >
                  Read Me - Best Practices using AI Assist
                </a>
              </div>
            )}
            {isTargetedDenialAnalysisLiveboard(props.liveboardName) && (
              <TargetedDenialAnalysisReports
                liveboardName={props.liveboardName}
              />
            )}
            <div id="ts-full-embed" />
          </div>
          {mdaePageTitle != undefined &&
                      (mdaePageTitle.toLowerCase() === "denial risks" ||
              mdaePageTitle?.toLowerCase() === "internal audit roi" ||
              props?.liveboardName?.toLowerCase() === "payerauditroi") && (
              <div className="button-container" style={{ right: buttonRight }}>
                <div
                  id="showCARCMappingButton"
                  className={"show-CARC-mapping-button"}
                  role="button"
                  onClick={(e) => {
                    onDrawerClick(isViewCARCMappingPanelOpen, dispatch);
                  }}
                >
                  {isFromROISummary ? "AI Summary" : "View CARC Mappings"}
                </div>
              </div>
            )}
        </div>
      </div>
    </ViewCARCMappingsPanel>    
  );
};

export default TsFullAppEmbed;
